import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/lib/utils";

import type { ExpenseCostImpactPreview } from "@artisancode/api-types";

interface CostImpactDialogProps {
  open: boolean;
  impact: ExpenseCostImpactPreview | null;
  isSubmitting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CostImpactDialog({
  open,
  impact,
  isSubmitting,
  onConfirm,
  onCancel,
}: CostImpactDialogProps) {
  if (!impact) return null;

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onCancel()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Konfirmasi Dampak Perubahan HPP Produk</DialogTitle>
          <DialogDescription>
            Belanja ini memperbarui biaya rata-rata bahan baku dan menghitung
            ulang HPP produk yang menggunakan resep terkait. Pastikan perubahan
            berikut sudah sesuai.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {impact.rawMaterials.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground">
                Perubahan Biaya Bahan Baku
              </h4>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bahan Baku</TableHead>
                      <TableHead className="text-right">Biaya Lama</TableHead>
                      <TableHead className="text-right">Biaya Baru</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {impact.rawMaterials.map((rm) => (
                      <TableRow key={rm.rawMaterialId}>
                        <TableCell className="font-medium">
                          {rm.rawMaterialName}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(rm.currentCost)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-primary">
                          {formatCurrency(rm.newCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {impact.products.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground">
                Dampak HPP Produk Terkait
              </h4>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produk</TableHead>
                      <TableHead className="text-right">HPP Lama</TableHead>
                      <TableHead className="text-right">HPP Baru</TableHead>
                      <TableHead className="text-right">Selisih</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {impact.products.map((p) => {
                      const diff = p.newCogs - p.currentCogs;
                      const diffSign = diff > 0 ? "+" : "";
                      return (
                        <TableRow key={p.productId}>
                          <TableCell className="font-medium">
                            {p.productName}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(p.currentCogs)}
                          </TableCell>
                          <TableCell className="text-right font-medium text-primary">
                            {formatCurrency(p.newCogs)}
                          </TableCell>
                          <TableCell
                            className={`text-right font-medium ${
                              diff > 0
                                ? "text-amber-600 dark:text-amber-400"
                                : diff < 0
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-muted-foreground"
                            }`}
                          >
                            {diffSign}
                            {formatCurrency(diff)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Batal
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? "Menyimpan..." : "Konfirmasi & Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
